import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { auth } from "../lib/api";
import { billingApi } from "../lib/billingApi";

/**
 * Hard gate de cobrança. Renderizado por App.js NO LUGAR de todo o CRM quando
 * brand.paymentBlocked === true. Nenhum módulo renderiza por trás.
 *
 * Duas saídas:
 *  - Cartão   → Stripe Checkout (assinatura) ou Portal do cliente. Resolve a
 *               cobrança recorrente de vez.
 *  - Pix      → Checkout avulso (modo payment). Ao confirmar (webhook), libera
 *               +N dias de acesso na hora, sem mexer na assinatura. Como Pix
 *               não confirma na hora, mostramos "aguardando" + polling.
 */
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const PaymentGate = () => {
    const { brand, setBrand } = useSalon();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState("");           // "checkout" | "portal" | "pix" | ""
    const params = new URLSearchParams(window.location.search);
    const billingParam = params.get("billing");     // success | pix-pending | cancel | null
    const [awaiting, setAwaiting] = useState(billingParam === "pix-pending" || billingParam === "success");
    const pollRef = useRef(null);

    const clearBillingParam = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("billing");
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.pathname + url.search);
    }, []);

    const refreshStatus = useCallback(async () => {
        try {
            const s = await billingApi.status();
            setStatus(s);
            if (s && s.access_blocked === false) {
                // desbloqueado — recarrega pra reentrar limpo no CRM
                setBrand((b) => ({ ...b, paymentBlocked: false, paymentStatus: s.payment_status || "ok" }));
                clearBillingParam();
                window.location.reload();
                return true;
            }
        } catch (_) { /* silencioso — mantém a tela */ }
        return false;
    }, [setBrand, clearBillingParam]);

    useEffect(() => { refreshStatus(); }, [refreshStatus]);

    // Polling enquanto aguarda confirmação (Pix) ou logo após voltar do Checkout.
    useEffect(() => {
        if (!awaiting) return;
        let stopped = false;
        const tick = async () => {
            if (stopped) return;
            const done = await refreshStatus();
            if (!done && !stopped) pollRef.current = setTimeout(tick, 5000);
        };
        pollRef.current = setTimeout(tick, 3000);
        // desiste do "aguardando" depois de 10 min (mantém o botão de tentar de novo)
        const giveUp = setTimeout(() => { setAwaiting(false); clearBillingParam(); }, 10 * 60 * 1000);
        return () => { stopped = true; clearTimeout(pollRef.current); clearTimeout(giveUp); };
    }, [awaiting, refreshStatus, clearBillingParam]);

    const go = async (kind) => {
        setBusy(kind);
        try {
            const fn = kind === "checkout" ? billingApi.checkout
                : kind === "portal" ? billingApi.portal
                : billingApi.pix;
            const { url } = await fn();
            if (url) window.location.href = url;      // sai do SPA pro Stripe
            else throw new Error("URL não retornada");
        } catch (err) {
            toast.error("Não foi possível abrir o pagamento", {
                description: err?.message || "Tente novamente em instantes.",
            });
            setBusy("");
        }
    };

    const logout = () => {
        auth.clear();
        window.location.href = "/";
    };

    const planName = status?.plan_name || brand.planName || brand.plan || "seu plano";
    const price = status?.plan_price_monthly ?? brand.planPriceMonthly ?? 0;
    const stripeReady = status?.stripe_configured !== false;
    const hasPrice = status?.has_stripe_price !== false;
    const hasSub = status?.has_stripe_subscription;
    const canceled = (status?.payment_status || brand.paymentStatus) === "canceled";

    return (
        <div
            data-testid="payment-gate"
            className="min-h-screen w-full bg-slate-950 text-white flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden"
        >
            <div
                className="absolute inset-0 opacity-40 pointer-events-none"
                style={{ background: "radial-gradient(at 20% 0%, rgba(217,119,6,0.16) 0, transparent 55%), radial-gradient(at 80% 100%, rgba(139,92,246,0.14) 0, transparent 55%)" }}
            />

            <div className="relative z-10 w-full max-w-2xl">
                <div className="flex flex-col items-center text-center mb-7">
                    <img src="/brand/kinng-mark-white.png" alt="Kinng" className="h-14 w-14 object-contain opacity-95 mb-3" />
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/80">
                        {canceled ? "Assinatura cancelada" : "Pagamento pendente"}
                    </div>
                    <h1 className="salon-heading text-2xl sm:text-3xl mt-1">
                        Regularize para continuar
                    </h1>
                    <p className="text-sm text-slate-400 mt-2 max-w-md">
                        {canceled
                            ? "Sua assinatura foi cancelada. Reative para voltar a usar o Kinng."
                            : "A cobrança da sua assinatura não foi concluída. Resolva abaixo — leva menos de 2 minutos."}
                        {" "}
                        <span className="text-slate-300">{planName}</span>
                        {price > 0 && <> · <span className="text-slate-300">{brl(price)}/mês</span></>}
                    </p>
                </div>

                {awaiting ? (
                    <div
                        data-testid="payment-gate-awaiting"
                        className="rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-8 text-center"
                    >
                        <i className="fa-solid fa-spinner fa-spin text-amber-400 text-2xl mb-3"></i>
                        <div className="salon-heading text-lg">Aguardando confirmação do pagamento…</div>
                        <p className="text-sm text-slate-400 mt-2">
                            Pode levar alguns instantes (Pix não é sempre instantâneo). Esta tela libera
                            sozinha assim que o pagamento cair.
                        </p>
                        <button
                            data-testid="payment-gate-recheck"
                            onClick={() => refreshStatus()}
                            className="mt-4 text-xs text-amber-300/90 hover:text-amber-200 uppercase tracking-wider"
                        >
                            Verificar agora
                        </button>
                        <div className="mt-4">
                            <button onClick={() => { setAwaiting(false); clearBillingParam(); }} className="text-xs text-slate-500 hover:text-slate-300">
                                voltar às opções de pagamento
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                        {/* Cartão */}
                        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] p-5 flex flex-col">
                            <div className="h-10 w-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 flex items-center justify-center mb-3">
                                <i className="fa-solid fa-credit-card"></i>
                            </div>
                            <div className="salon-heading text-lg">
                                {hasSub ? "Atualizar cartão" : "Assinar com cartão"}
                            </div>
                            <p className="text-xs text-slate-400 mt-1 flex-1">
                                {hasSub
                                    ? "Corrige o cartão e refaz a cobrança. A renovação volta a ser automática."
                                    : "Cobrança mensal automática, renova sozinha. Cancele quando quiser."}
                            </p>
                            {hasSub ? (
                                <button
                                    data-testid="payment-gate-portal"
                                    onClick={() => go("portal")}
                                    disabled={!!busy || !stripeReady}
                                    className="mt-4 min-h-[46px] rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-[0.15em] disabled:opacity-50"
                                >
                                    {busy === "portal" ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Abrindo…</> : "Gerenciar assinatura"}
                                </button>
                            ) : (
                                <button
                                    data-testid="payment-gate-checkout"
                                    onClick={() => go("checkout")}
                                    disabled={!!busy || !stripeReady || !hasPrice}
                                    className="mt-4 min-h-[46px] rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-[0.15em] disabled:opacity-50"
                                >
                                    {busy === "checkout" ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Abrindo…</> : "Assinar agora"}
                                </button>
                            )}
                            {hasSub && (
                                <button
                                    data-testid="payment-gate-checkout-alt"
                                    onClick={() => go("checkout")}
                                    disabled={!!busy || !stripeReady || !hasPrice}
                                    className="mt-2 text-[11px] text-emerald-300/80 hover:text-emerald-200 uppercase tracking-wider disabled:opacity-40"
                                >
                                    ou refazer a assinatura do zero
                                </button>
                            )}
                        </div>

                        {/* Pix */}
                        <div className="rounded-2xl border border-sky-500/25 bg-sky-500/[0.05] p-5 flex flex-col">
                            <div className="h-10 w-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-300 flex items-center justify-center mb-3">
                                <i className="fa-solid fa-qrcode"></i>
                            </div>
                            <div className="salon-heading text-lg">Pagar via Pix agora</div>
                            <p className="text-xs text-slate-400 mt-1 flex-1">
                                Libera <strong className="text-slate-200">{status?.pix_recovery_days || 30} dias</strong> de acesso
                                na hora, sem precisar mexer no cartão. Pagamento avulso — a assinatura
                                do cartão continua como está.
                            </p>
                            <button
                                data-testid="payment-gate-pix"
                                onClick={() => go("pix")}
                                disabled={!!busy || !stripeReady || !(price > 0)}
                                className="mt-4 min-h-[46px] rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs uppercase tracking-[0.15em] disabled:opacity-50"
                            >
                                {busy === "pix" ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Gerando Pix…</>
                                    : price > 0 ? `Pagar ${brl(price)} via Pix` : "Pagar via Pix"}
                            </button>
                        </div>
                    </div>
                )}

                {!stripeReady && (
                    <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs px-4 py-3">
                        <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                        Pagamento online ainda não está configurado nesta conta. Fale com o suporte.
                    </div>
                )}
                {stripeReady && !hasPrice && !hasSub && (
                    <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs px-4 py-3">
                        <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                        Este plano ainda não tem o preço configurado no gateway. Use o Pix ou fale com o suporte.
                    </div>
                )}

                <div className="mt-7 text-center">
                    <button
                        data-testid="payment-gate-logout"
                        onClick={logout}
                        className="text-xs text-slate-500 hover:text-slate-300 uppercase tracking-wider"
                    >
                        <i className="fa-solid fa-arrow-right-from-bracket mr-1.5"></i>Sair
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentGate;
